import React from 'react';
import { ClassValue } from 'classnames/types';
import Typography from '../Typography';
import Link from '../../Link/Link';
import styles from './TextLink.module.scss';
import { ArrayElementType } from '../../../types/utils/ArrayElementType';
import { SupportedColor as SupportedElementColor } from '../../Element/Element';
import { CreateProps } from '../../../types/utils/CreateProps';

export type TextLinkProps = CreateProps<
  {
    /** Size options for the TextLink */
    size?: SupportedTextLinkSize;
    /** Colors options for the TextLink. Colors are mapped with a suiting hover color */
    color?: SupportedTextLinkColor;
    className?: ClassValue;
  },
  typeof Link,
  'color' | 'hoverColor' | 'className' | 'fontWeight'
>;
// Alias for backwards compatibility
export type TextLinkAttributes = TextLinkProps;

const hoverColorMap: {
  [Key in SupportedTextLinkColor]: SupportedElementColor;
} = {
  gray800: 'purple500',
  black: 'purple500',
  red600: 'red400',
  purple500: 'purple400',
};

const TextLink: React.FunctionComponent<TextLinkProps> = ({
  size = 'md',
  color = 'purple500',
  className,
  children,
  ...props
}) => {
  const classNames: ClassValue = [
    styles[`textlink__size_${size.toLowerCase()}`],
    styles[`textlink`],
    className,
  ];

  return (
    <Link {...props} color={color} hoverColor={hoverColorMap[color]}>
      <Typography
        font="teliasans"
        className={classNames}
        uppercase={false}
        fontWeight="normal"
      >
        {children}
      </Typography>
    </Link>
  );
};

export const supportedTextLinkSizes = ['lg', 'md', 'sm'] as const;

export type SupportedTextLinkSize = ArrayElementType<
  typeof supportedTextLinkSizes
>;

export const supportedTextLinkColors = [
  'purple500',
  'gray800',
  'black',
  'red600',
] as const;

export type SupportedTextLinkColor = ArrayElementType<
  typeof supportedTextLinkColors
>;

export default TextLink;
